import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import intlTelInput from 'intl-tel-input';
import type { Iti } from 'intl-tel-input';
import type { Iso2 } from 'intl-tel-input/data';

// Phone number types for validation (not exported from main module)
type NumberType =
  | 'FIXED_LINE_OR_MOBILE'
  | 'FIXED_LINE'
  | 'MOBILE'
  | 'PAGER'
  | 'PERSONAL_NUMBER'
  | 'PREMIUM_RATE'
  | 'SHARED_COST'
  | 'TOLL_FREE'
  | 'UAN'
  | 'UNKNOWN'
  | 'VOICEMAIL'
  | 'VOIP';

// Phone number format types (custom for this component)
type NumberFormat = 'E164' | 'INTERNATIONAL' | 'NATIONAL' | 'RFC3966';

// Format constants from intl-tel-input (hardcoded as they're stable)
const NUMBER_FORMAT = {
  E164: 0,
  INTERNATIONAL: 1,
  NATIONAL: 2,
  RFC3966: 3,
} as const;

interface PhoneInputProps extends FormlyFieldProps {
  initialCountry?: Iso2 | 'auto' | '';
  formatOnDisplay?: boolean;
  nationalMode?: boolean;
  separateDialCode?: boolean;
  strictMode?: boolean;
  countryOrder?: Iso2[];
  onlyCountries?: Iso2[];
  excludeCountries?: Iso2[];
  validationNumberTypes?: NumberType[];
  isValidNumberPrecise?: boolean;
  valueFormat?: NumberFormat; // Format for stored form value
  allowDropdown?: boolean; // Enable/disable country dropdown
  showFlags?: boolean; // Show/hide flag icon
  containerClass?: string; // Custom class for container
  // Input group support for buttons and text
  prependText?: string;
  prependTextClass?: string;
  appendText?: string;
  appendTextClass?: string;
  prependButtonText?: string;
  prependButtonCommand?: (field: any, value?: any) => void;
  prependButtonClass?: string;
  appendButtonText?: string;
  appendButtonCommand?: (field: any, value?: any) => void;
  appendButtonClass?: string;
}

@Component({
  standalone: false,
  selector: 'formly-field-phone-input',
  templateUrl: './phone-input.component.html',
})
export class FormlyFieldPhoneInput
  extends FieldType<FieldTypeConfig<PhoneInputProps>>
  implements AfterViewInit, OnDestroy {
  @ViewChild('phoneInput', { static: false }) phoneInputRef!: ElementRef<HTMLInputElement>;

  private iti: Iti | null = null;
  private isInitialized = false;
  private isUpdatingFromIti = false;
  private readonly destroy$ = new Subject<void>();

  override defaultOptions = {
    props: {
      initialCountry: 'us' as Iso2,
      formatOnDisplay: true,
      nationalMode: true,
      strictMode: true,
      valueFormat: 'E164' as NumberFormat, // Store in E164 format by default
    },
  };

  ngAfterViewInit(): void {
    // Store reference to self for easy access from hooks
    (this.field as any).phoneComponent = this;

    // Delay initialization to ensure DOM is fully ready
    setTimeout(() => this.initializeIntlTelInput(), 0);
  }

  ngOnDestroy(): void {
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Public method to set the country programmatically
   * @param countryCode ISO2 country code (e.g., 'us', 'gb')
   */
  public setCountry(countryCode: string): void {
    if (this.iti) {
      try {
        this.isUpdatingFromIti = true;
        this.iti.setCountry(countryCode.toLowerCase() as Iso2);
      } finally {
        this.isUpdatingFromIti = false;
      }
    }
  }

  /**
   * Public method to get the currently selected country code
   * @returns ISO2 country code (e.g., 'us', 'gb') or empty string if not initialized
   */
  public getCountryCode(): string {
    if (this.iti) {
      const countryData = this.iti.getSelectedCountryData();
      return countryData?.iso2?.toUpperCase() || '';
    }
    return '';
  }

  private initializeIntlTelInput(): void {
    if (!this.phoneInputRef || this.isInitialized) {
      return;
    }

    const input = this.phoneInputRef.nativeElement;

    // Build configuration object - only include array properties if they have values
    const config: any = {
      initialCountry: this.props.initialCountry ?? 'us',
      formatOnDisplay: this.props.formatOnDisplay ?? true,
      nationalMode: this.props.nationalMode ?? true,
      separateDialCode: this.props.separateDialCode ?? false,
      strictMode: this.props.strictMode ?? true,
      validationNumberTypes: this.props.validationNumberTypes ?? ['MOBILE'],
      allowDropdown: this.props.allowDropdown ?? true,
      showFlags: this.props.showFlags ?? true,
      containerClass: this.props.containerClass ?? '',
      loadUtils: () => import('intl-tel-input/utils'),
    };

    // Only add array properties if they are defined and not empty
    if (this.props.countryOrder && this.props.countryOrder.length > 0) {
      config.countryOrder = this.props.countryOrder;
    }
    if (this.props.onlyCountries && this.props.onlyCountries.length > 0) {
      config.onlyCountries = this.props.onlyCountries;
    }
    if (this.props.excludeCountries && this.props.excludeCountries.length > 0) {
      config.excludeCountries = this.props.excludeCountries;
    }

    // Initialize intl-tel-input
    this.iti = intlTelInput(input, config);

    this.isInitialized = true;

    // Set initial value if it exists
    if (this.formControl.value) {
      try {
        this.isUpdatingFromIti = true;
        this.iti.setNumber(this.formControl.value);
      } finally {
        this.isUpdatingFromIti = false;
      }
    }

    // Listen for input changes and update form control
    input.addEventListener('input', () => {
      this.onPhoneNumberChange();
    });

    // Listen for country changes
    input.addEventListener('countrychange', () => {
      this.onPhoneNumberChange();
    });

    // Listen to external form control changes (e.g., form reset)
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.iti && !this.isUpdatingFromIti && value !== this.iti.getNumber(NUMBER_FORMAT[this.props.valueFormat ?? 'E164'])) {
          try {
            this.isUpdatingFromIti = true;
            this.iti.setNumber(value || '');
          } finally {
            this.isUpdatingFromIti = false;
          }
        }
      });
  }

  private onPhoneNumberChange(): void {
    if (!this.iti || this.isUpdatingFromIti) {
      return;
    }

    this.isUpdatingFromIti = true;

    // Get number in specified value format (backwards compatible with numberFormat)
    const formatName = this.props.valueFormat ?? 'E164';
    const format = NUMBER_FORMAT[formatName];
    const number = this.iti.getNumber(format);

    // Update form control (emit event so parent components can react to changes)
    this.formControl.setValue(number);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();

    // Validate using intl-tel-input (internally waits for utils if needed)
    let isValid: boolean;
    if (this.props.isValidNumberPrecise) {
      isValid = this.iti.isValidNumberPrecise();
    } else {
      isValid = this.iti.isValidNumber();
    }

    // Set or clear validation error
    const currentErrors = this.formControl.errors || {};
    if (!isValid && number) {
      this.formControl.setErrors({
        ...currentErrors,
        internationalPhone: true
      });
    } else {
      // Remove internationalPhone error if valid
      const { internationalPhone, ...remainingErrors } = currentErrors;
      if (Object.keys(remainingErrors).length > 0) {
        this.formControl.setErrors(remainingErrors);
      } else {
        this.formControl.setErrors(null);
      }
    }

    this.isUpdatingFromIti = false;
  }

  onPrependButtonClick() {
    if (this.props.prependButtonCommand && typeof this.props.prependButtonCommand === 'function') {
      this.props.prependButtonCommand(this.field, this.formControl.value);
    }
  }

  onAppendButtonClick() {
    if (this.props.appendButtonCommand && typeof this.props.appendButtonCommand === 'function') {
      this.props.appendButtonCommand(this.field, this.formControl.value);
    }
  }
}